import {
  Box,
  Button,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Modal,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useSelector } from "react-redux";
import jsPDF from "jspdf";

const PaymentStatement: React.FC<any> = () => {
  const formatAmount = (amount: any) => {
    return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const getToday = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  const [formData, setFormData] = useState({
    user: "",
    dateFrom: getToday(),
    dateTo: getToday(),
    slipNo: "",
  });

  const [users, setUsers] = useState<any[]>([]);
  const [statementData, setStatementData] = useState<any>({
    statement: [],
    pageHeaderData: null,
  });
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const projectProfile = useSelector(
    (state: any) => state.projectProfileSlice.projectProfile
  );

  const isValidObjectId = (id: string) => {
    return /^[0-9a-fA-F]{24}$/.test(id);
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | { name?: string; value: unknown }
    >
  ) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name as string]: value,
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name as string]: value,
    }));
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setPdfUrl(null);
  };

  const generatePDF = () => {
    console.log("Generating PDF with statementData:", statementData);

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 10;
    let yPos = 20;

    const headerY = 10;
    const logoX = 50;
    const logoWidth = 15;
    const logoHeight = 15;
    const textStartX = logoX + logoWidth;

    const pageHeaderData = statementData.pageHeaderData || {};
    const headerLogo = pageHeaderData.logo || "";
    const headerTitle = pageHeaderData.title || "";
    const headerAddress = pageHeaderData.address || "";
    const headerPhone = pageHeaderData.phone || "";

    if (headerLogo || headerTitle || headerAddress || headerPhone) {
      if (headerLogo) {
        try {
          doc.addImage(
            headerLogo,
            "JPEG",
            logoX,
            headerY,
            logoWidth,
            logoHeight
          );
        } catch (error) {
          console.error("Error adding logo to PDF:", error);
        }
      }

      if (headerTitle) {
        doc.setFont("times", "bold");
        doc.setFontSize(18);
        doc.text(headerTitle.toUpperCase(), textStartX, headerY + 7);

        const titleWidth = doc.getTextWidth(headerTitle.toUpperCase());

        // Add horizontal line under the title (matching title width)
        doc.setDrawColor(0);
        doc.setLineWidth(1);
        doc.line(
          textStartX,
          headerY + 9,
          textStartX + titleWidth + 2,
          headerY + 9
        );
      }

      if (headerAddress || headerPhone) {
        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        const combinedText = `${headerAddress.trim()} ${
          headerPhone ? `(Ph: ${headerPhone.trim()})` : ""
        }`.trim();
        if (combinedText) {
          doc.text(combinedText, textStartX, headerY + 14);
        }
      }

      doc.setDrawColor(0);
      doc.line(margin, headerY + 25, pageWidth - margin, headerY + 25);
      const addressWidth = doc.getTextWidth(headerAddress || "");
      // Add line under address (matching address width)
      doc.setDrawColor(0);
      doc.setLineWidth(0.1);
      doc.line(
        textStartX,
        headerY + 15.1,
        textStartX + addressWidth,
        headerY + 15.1
      );
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    const title = "BLOOD BANK STATEMENT";
    const textWidth = doc.getTextWidth(title);
    const textX = pageWidth / 2;
    const textY = headerY + 35;
    doc.text(title, textX, textY, { align: "center" });
    doc.line(
      textX - textWidth / 2,
      textY + 1,
      textX + textWidth / 2,
      textY + 1
    );
    yPos = textY + 8;

    doc.setFontSize(10);
    doc.text(`User Name: ${formData.user || "ALL USERS"}`, margin, yPos);
    doc.text(`Date From: ${formData.dateFrom || "N/A"}`, pageWidth / 3, yPos);
    doc.text(`Date To: ${formData.dateTo || "N/A"}`, (pageWidth * 2) / 3, yPos);
    yPos += 5;

    if (statementData.statement.length > 0) {
      const autoTable = require("jspdf-autotable").default;

      const totalAmount = statementData.statement.reduce(
        (sum: number, row: any) =>
          sum + parseFloat(row.amount?.toString() || "0"),
        0
      );
      const totalDiscount = statementData.statement.reduce(
        (sum: number, row: any) =>
          sum + parseFloat(row.discount?.toString() || "0"),
        0
      );
      const totalPaid = statementData.statement.reduce(
        (sum: number, row: any) =>
          sum + parseFloat(row.paid?.toString() || "0"),
        0
      );
      const totalDue = 0;

      autoTable(doc, {
        startY: yPos,
        head: [
          [
            "SN",
            "Date",
            "Slip No",
            "Patient Name",
            "Amount",
            "Discount",
            "Paid",
            "Due",
          ],
        ],
        body: statementData.statement.map((row: any, index: number) => [
          (index + 1).toString(),
          row.date?.toString() || "N/A",
          row.slipNo?.toString() || "N/A",
          row.patientName?.toString() || "N/A",
          row.amount ? formatAmount(row.amount) : "0",
          row.discount ? formatAmount(row.discount) : "0",
          row.paid ? formatAmount(row.paid) : "0",
          "0",
        ]),
        styles: {
          font: "helvetica",
          fontSize: 10,
          lineWidth: 0,
          lineColor: [0, 0, 0],
        },
        headStyles: {
          fillColor: [200, 200, 200],
          textColor: [0, 0, 0],
          fontStyle: "bold",
          lineWidth: 0,
          lineColor: [0, 0, 0],
        },
        alternateRowStyles: {
          fillColor: false,
        },
        margin: { left: margin, right: margin },
        didDrawCell: function (data: any) {
          const { cell, column, table } = data;
          const { x, y, width, height } = cell;
          const isFirst = column.index === 0;
          const isLast = column.index === table.columns.length - 1;

          if (isFirst) {
            doc.setLineWidth(0.2);
            doc.line(x, y, x, y + height);
          }

          if (isLast) {
            doc.setLineWidth(0.1);
            doc.line(x + width, y, x + width, y + height);
          }

          doc.setLineWidth(0.1);
          doc.line(x, y, x + width, y);
          doc.line(x, y + height, x + width, y + height);
        },
      });

      yPos = (doc.lastAutoTable?.finalY ?? yPos) + 10;
      const usableWidth = pageWidth - margin * 2;
      const thirdWidth = usableWidth / 3;

      const leftStartX = margin;
      const leftEndX = margin + thirdWidth;
      let rightStartX = margin + thirdWidth * 2 - 16.7;
      const rightEndX = pageWidth - margin;
      if (rightStartX < 0) rightStartX = 0;

      doc.setLineWidth(0.1);
      doc.line(rightStartX, yPos - 4.6, rightEndX, yPos - 4.6);
      doc.line(rightStartX, yPos - 4, rightEndX, yPos - 4);

      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text(`TOTAL COLLECTION`, margin, yPos);
      doc.text(formatAmount(totalPaid.toFixed(2)), pageWidth - 140, yPos, {
        align: "right",
      });
      doc.text(formatAmount(totalAmount.toFixed(2)), pageWidth - 75, yPos, {
        align: "right",
      });
      doc.text(
        formatAmount(totalDiscount.toFixed(2)),
        (pageWidth * 2) / 3 + 15,
        yPos,
        { align: "right" }
      );
      doc.text(
        formatAmount(totalPaid.toFixed(2)),
        (pageWidth * 5) / 6 + 7,
        yPos,
        { align: "right" }
      );
      doc.text(formatAmount(totalDue.toFixed(2)), pageWidth - margin, yPos, {
        align: "right",
      });

      yPos += 5;
      doc.line(rightStartX, yPos - 3.6, rightEndX, yPos - 3.6);
      doc.line(rightStartX, yPos - 3, rightEndX, yPos - 3);
      yPos += 5;

      doc.text(`TOTAL PATIENT`, margin, yPos);
      doc.text(statementData.statement.length.toString(), margin + 45, yPos);
    } else {
      console.log("No statement data available for PDF");
      doc.setFontSize(12);
      doc.text("No statement data available.", pageWidth / 2, yPos, {
        align: "center",
      });
    }

    // ✅ Print using hidden iframe
    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);

    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = pdfUrl;

    iframe.onload = function () {
      setTimeout(() => {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      }, 500);
    };

    document.body.appendChild(iframe);
  };

  // Trigger PDF generation when statementData updates
  useEffect(() => {
    if (statementData.statement.length > 0) {
      generatePDF();
    }
  }, [statementData]);

  const handleSubmit = async () => {
    if (!formData.dateFrom || !formData.dateTo) {
      toast.error("Please fill in the required date fields");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      console.log("Token:", token);
      if (!token) {
        toast.error("Authentication token missing. Please log in.");
        return;
      }

      let userIds: string[] = [];
      if (formData.user) {
        if (!isValidObjectId(formData.user)) {
          toast.error("Selected user ID is invalid.");
          return;
        }
        userIds = [formData.user];
      }

      const requestBody = {
        exta: {},
        userIds,
        fromDate: formData.dateFrom,
        toDate: formData.dateTo,
        slipNo: formData.slipNo || "",
      };
      console.log("Request Body:", requestBody);

      const response = await axios.post(
        `${process.env.REACT_APP_BLOOD_BANK}/patient-slip/statement`,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          timeout: 10000,
        }
      );

      console.log("API Response:", response.data);
      if (response.data.isSuccess) {
        setStatementData({
          statement: response.data.data.statement || [],
          pageHeaderData: response.data.data.pageHeaderData || null,
        });
      } else {
        toast.info(`${response.data.message || "info"}`);
      }
    } catch (error: any) {
      console.error("Axios Error:", error);
      if (error.code === "ERR_NETWORK") {
        toast.error(
          "Network Error: Server is unreachable or CORS issue detected."
        );
      } else if (error.response) {
        toast.error(
          `Error ${error.response.status}: ${
            error.response.data.message || "Server error"
          }`
        );
      } else if (error.request) {
        toast.error("No response from server. Check network or server status.");
      } else {
        toast.error(`Error: ${error.message || "Something went wrong"}`);
      }
    }
  };

  return (
    <Box sx={{ height: "100vh", bgcolor: "#f5f5f5" }}>
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#f5f5f5",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          p: 4,
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: "bold", mb: 2, color: "#333", textAlign: "left" }}
        >
          Blood Bank Payment Statement
        </Typography>
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: 2,
              p: 4,
              boxShadow: 2,
              border: "1px solid #e0e0e0",
              width: "100%",
              maxWidth: "600px",
            }}
          >
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel
                sx={{
                  top: "-5px", // adjust based on height
                }}
                id="user-select-label"
              >
                Select User
              </InputLabel>
              <Select
                className="custom-input"
                labelId="user-select-label"
                name="user"
                value={formData.user}
                label="Select User (Optional)"
                onChange={handleSelectChange}
              >
                <MenuItem value="">None</MenuItem>
                {users.map(user => (
                  <MenuItem key={user._id} value={user._id}>
                    {user.name || `User ${user._id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              className="custom-input"
              size="small"
              label="Date From"
              type="date"
              name="dateFrom"
              value={formData.dateFrom}
              fullWidth
              onChange={handleChange}
              sx={{ mb: 2 }}
              InputLabelProps={{ shrink: true }}
              required
            />

            <TextField
              className="custom-input"
              size="small"
              label="Date To"
              type="date"
              name="dateTo"
              value={formData.dateTo}
              fullWidth
              onChange={handleChange}
              sx={{ mb: 2 }}
              InputLabelProps={{ shrink: true }}
              required
            />

            <TextField
              className="custom-input"
              size="small"
              label="Slip No"
              name="slipNo"
              value={formData.slipNo}
              fullWidth
              onChange={handleChange}
              sx={{ mb: 2 }}
            />

            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleSubmit}
              disabled={!formData.dateFrom || !formData.dateTo}
              sx={{
                bgcolor: "#1976d2",
                "&:hover": { bgcolor: "#1565c0" },
                textTransform: "uppercase",
                fontWeight: "bold",
              }}
            >
              Print Statement
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PaymentStatement;
